import styles from '../ScenarioGenerator.module.css';
import { Tooltip } from '../../../common/Tooltip';
import { brandConfig } from '../../../../config/brands';
import type { SetupPanelProps } from '../SetupPanel';
import { useScenarioSetup } from '../hooks/ScenarioSetupContext';

export function ProductSection(props: SetupPanelProps) {
  const {
    product, accordions,
    mutation, videoGenerationMutation,
    productSelectRef, showProductTooltip, setShowProductTooltip,
    setIsProductSelectorOpen,
    productImageFetchError, setProductImageFetchError, productImageFetching,
    clearAssets,
    asinDisabled, customDisabled } = props;

  const { setMode, scenarioInputTab, setScenarioInputTab } = useScenarioSetup();

  const {
    asin, setAsin, savedAsin,
    manualProductId, setManualProductId, manualProductTitle, setManualProductTitle, manualProductImageUrl, setManualProductImageUrl,
    productName, setProductName, brandName, setBrandName, sellingPoints, setSellingPoints,
    setFetchedAsins, setFetchedProductIds,
    selectedProduct,
    productSummary, customProductSummary } = product;

  const {
    isProductSectionOpen, setIsProductSectionOpen } = accordions;

  return (
    <>
          <div className={`accordion-section ${isProductSectionOpen ? 'accordion-section-open' : ''}`}>
            <button
              type="button"
              className="accordion-header"
              onClick={() => setIsProductSectionOpen(!isProductSectionOpen)}
              aria-expanded={isProductSectionOpen}
            >
              <span className="accordion-title">
                <img src="/gift_box_1.svg" alt="" className="accordion-title-icon" />
                <span style={{ whiteSpace: 'nowrap' }}>プロモーション対象</span>
                <span className="accordion-summary">{scenarioInputTab === 'product' ? productSummary : customProductSummary}</span>
              </span>
              <span className={`accordion-icon ${isProductSectionOpen ? 'accordion-icon-open' : ''}`}>
                <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" style={{ transition: 'transform 0.2s', transform: isProductSectionOpen ? 'rotate(90deg)' : 'none' }}><path d="M2 0l4 4-4 4z" /></svg>
              </span>
            </button>
            <div className={`accordion-panel ${isProductSectionOpen ? 'accordion-panel-open' : 'accordion-panel-closed'}`}>
              <div className={styles.productPanelStack}>
                {/* 手入力ウィンドウ表示中は背面を inert 化 (z-index で覆うだけでは Tab フォーカスが通る) */}
                <div className={styles.productPanelContent} inert={scenarioInputTab === 'manual'}>
                  {!asin && !manualProductId && (
                    <div className={styles.productPanelTopRow}>
                      <p className={styles.productSelectNote}>{brandConfig.labels.productShort}を選択してください</p>
                      <Tooltip content={`${brandConfig.labels.productShort}情報を手入力`}>
                        <button
                          type="button"
                          className={styles.iconButton}
                          onClick={() => {
                            setScenarioInputTab('manual');
                            setMode('custom');
                            setShowProductTooltip(false);
                          }}
                          disabled={mutation.isPending || videoGenerationMutation.isPending}
                        >
                          <img src="/content_ink_pen_write.svg" alt={`${brandConfig.labels.productShort}情報を手入力`} style={{ width: '16px', height: '16px' }} />
                        </button>
                      </Tooltip>
                    </div>
                  )}

              {!asin && !manualProductId ? (
                <div className={styles.productSelectActions}>
                  <button
                    ref={productSelectRef}
                    type="button"
                    className={styles.productSelectButton}
                    onClick={() => setIsProductSelectorOpen(true)}
                    disabled={mutation.isPending || videoGenerationMutation.isPending}
                  >
                    {showProductTooltip && (
                      <div className={styles.productTooltip}>{brandConfig.labels.productShort}を選択してください</div>
                    )}
                    {brandConfig.labels.productShort}を選択 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}><polyline points="9 18 15 12 9 6" /></svg>
                  </button>
                  {savedAsin && (
                    <span
                      className={styles.modeButtonLink}
                      onClick={() => {
                        clearAssets();
                        // 素材をクリアしたら取得済みフラグも消し、自動補完を再実行させる
                        // (残すと scope が空のまま生成され商品が消える)。
                        setFetchedAsins((prev) => {
                          const next = new Set(prev);
                          next.delete(savedAsin);
                          return next;
                        });
                        setAsin(savedAsin);
                      }}
                    >
                      前回選択した商品を使用
                    </span>
                  )}
                </div>
              ) : asin ? (
                <div className={styles.productBox}>
                  {selectedProduct?.main_image_url && (
                    <img src={selectedProduct.main_image_url} alt={selectedProduct.title} />
                  )}
                  <div className={styles.productBoxInfo}>
                    <span className={styles.productBoxTitle}>
                      {selectedProduct?.title || asin}
                    </span>
                    <span className={styles.productBoxMeta}>{asin}</span>
                    <a
                      href={`https://www.amazon.co.jp/dp/${asin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.productBoxLink}
                    >
                      Amazonで確認 →
                    </a>
                  </div>
                  <div className={styles.productBoxActions}>
                    <button
                      type="button"
                      className={styles.productBoxRemove}
                      onClick={() => {
                        const currentAsin = asin;
                        setAsin('');
                        clearAssets();
                        setProductImageFetchError(null);
                        setFetchedAsins((prev) => {
                          const next = new Set(prev);
                          next.delete(currentAsin);
                          return next;
                        });
                      }}
                      disabled={asinDisabled}
                    >
                      ✕
                    </button>
                  </div>
                  {productImageFetchError && !productImageFetching && (
                    <div className={styles.productBoxError}>
                      <span className={styles.productBoxErrorText}>{productImageFetchError}</span>
                      <button
                        type="button"
                        className={styles.productBoxRetryBtn}
                        onClick={() => {
                          setProductImageFetchError(null);
                          setFetchedAsins((prev) => {
                            const next = new Set(prev);
                            next.delete(asin);
                            return next;
                          });
                        }}
                      >
                        再取得
                      </button>
                    </div>
                  )}
                  {productImageFetching && (
                    <div className={styles.productBoxFetching}>
                      商品画像を取得中...
                    </div>
                  )}
                </div>
              ) : manualProductId ? (
                <div className={styles.productBox}>
                  {manualProductImageUrl && (
                    <img src={manualProductImageUrl} alt={manualProductTitle || 'マニュアル登録商品'} />
                  )}
                  <div className={styles.productBoxInfo}>
                    <span className={styles.productBoxTitle}>
                      {manualProductTitle || 'マニュアル登録商品'}
                    </span>
                    <span className={styles.productBoxMeta}>マニュアル登録</span>
                  </div>
                  <div className={styles.productBoxActions}>
                    <button
                      type="button"
                      className={styles.productBoxRemove}
                      onClick={() => {
                        const currentProductId = manualProductId;
                        if (currentProductId) {
                          setFetchedProductIds((prev) => {
                            const next = new Set(prev);
                            next.delete(currentProductId);
                            return next;
                          });
                        }
                        setManualProductId(null);
                        setManualProductTitle(null);
                        setManualProductImageUrl(null);
                        clearAssets();
                        setProductImageFetchError(null);
                        setMode('asin');
                      }}
                      disabled={asinDisabled}
                    >
                      ✕
                    </button>
                  </div>
                  {productImageFetchError && !productImageFetching && (
                    <div className={styles.productBoxError}>
                      <span className={styles.productBoxErrorText}>{productImageFetchError}</span>
                      <button
                        type="button"
                        className={styles.productBoxRetryBtn}
                        onClick={() => {
                          setProductImageFetchError(null);
                          setFetchedProductIds((prev) => {
                            const next = new Set(prev);
                            next.delete(manualProductId);
                            return next;
                          });
                        }}
                      >
                        再取得
                      </button>
                    </div>
                  )}
                  {productImageFetching && (
                    <div className={styles.productBoxFetching}>
                      商品画像を取得中...
                    </div>
                  )}
                </div>
              ) : null}
                  <div className={styles.accordionPanelFooter}>
                    <Tooltip content="閉じる">
                      <button type="button" className={styles.iconButton} onClick={() => setIsProductSectionOpen(false)}>
                        <img src="/move_collapse_vertical.svg" alt="閉じる" style={{ width: '16px', height: '16px' }} />
                      </button>
                    </Tooltip>
                  </div>
                </div>

                {scenarioInputTab === 'manual' && (
                  <div className={styles.manualEntryWindow}>
                    <div className={styles.manualEntryHeader}>
                      <img src="/content_ink_pen_write.svg" alt="" className={styles.manualEntryTitleIcon} />
                      <span className={styles.manualEntryTitle}>{brandConfig.labels.productShort}情報を手入力</span>
                      <button
                        type="button"
                        className={styles.manualEntryClose}
                        onClick={() => {
                          setScenarioInputTab('product');
                          setMode(manualProductId ? 'manual' : 'asin');
                        }}
                        disabled={mutation.isPending || videoGenerationMutation.isPending}
                        aria-label="閉じる"
                      >
                        ✕
                      </button>
                    </div>
                    <p className={styles.manualEntryDescription}>
                      {brandConfig.labels.product}には登録せず、都度手入力によって再利用しない{brandConfig.labels.scenarioGenerate}します。
                    </p>
              <div className="form-grid">
                <label className="gen-label">
                  <span>{brandConfig.labels.productName}</span>
                  <input
                    type="text"
                    value={productName}
                    onChange={(event) => setProductName(event.target.value)}
                    placeholder="例: プレミアムドリップコーヒー"
                    disabled={customDisabled}
                  />
                </label>
                <label className="gen-label">
                  <span>ブランド名</span>
                  <input
                    type="text"
                    value={brandName}
                    onChange={(event) => setBrandName(event.target.value)}
                    placeholder="任意"
                    disabled={customDisabled}
                  />
                </label>
              </div>
              <label className="gen-label">
                <span>{brandConfig.labels.productShort}説明（1 行につき 1 項目）</span>
                <textarea
                  value={sellingPoints}
                  onChange={(event) => setSellingPoints(event.target.value)}
                  rows={6}
                  placeholder={`例:
・職人が一つひとつ手作りした上品なマグカップ
・電子レンジ・食洗機対応で日常使いに最適
・目的: 母の日ギフト向けの販促
・ターゲット: 30〜50代女性
・価格: 3,980円（期間限定 20%OFF）
・キャンペーン: 2点以上購入で送料無料`}
                  disabled={customDisabled}
                />
              </label>
              {/* 目的・ターゲット・価格帯・オファーは商品説明に統合 */}
                  </div>
                )}
              </div>
            </div>
          </div>
    </>
  );
}
